'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function AlinBotoksuPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/alin-botoksu-nedir';
  const pageTitle = 'Alın Botoksu Nedir? – Kırışıklıklara Kalıcı Çözüm Mümkün mü?';
  const pageDescription = 'Alın botoksu nedir, nasıl yapılır? Alın çizgilerine kalıcı çözüm ve fiyatları hakkında detaylı bilgiler.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Alın Botoksu Nedir?', href: '/tr/blog/alin-botoksu-nedir' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Alın botoksu uygulaması sırasında enjeksiyon yapılan hasta"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Estetik Tedaviler
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Alın Botoksu Nedir? – Kırışıklıklara Kalıcı Çözüm Mümkün mü?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>16 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Mimik kullandıkça oluşan alın çizgileri, yaşla birlikte derinleşerek estetik kaygılara yol açar. Alın botoksu, bu çizgilere karşı en etkili çözümlerden biridir. Bu yazıda alın botoksunun ne olduğunu, nasıl yapıldığını ve kimler için uygun olduğunu detaylıca açıklıyoruz.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Alın Botoksu Nedir?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Alın botoksu, alın bölgesinde yer alan yüzeysel kaslara botulinum toksini enjeksiyonu yapılarak mimik kaynaklı kırışıklıkların giderilmesini sağlayan bir işlemdir. Temel hedef, kasların geçici olarak gevşetilmesi ve alın bölgesinin daha pürüzsüz görünmesidir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Hangi Durumlarda Tercih Edilir?
              </h2>
              
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li>Yatay alın çizgileri belirginse</li>
                  <li>Sürekli mimik kullanan kişilerde dinamik kırışıklıklar oluşmuşsa</li>
                  <li>Yaşla birlikte alın çizgileri derinleşmişse</li>
                  <li>Daha genç ve dinamik bir görünüm isteniyorsa</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Alın Botoksu Nasıl Yapılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Uygulama oldukça basittir ve genellikle 10 dakikadan kısa sürer:
              </p>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Değerlendirme:</h3>
                  <p className="text-gray-700">Hekim, mimik hareketlerini gözlemleyerek enjeksiyon yapılacak kasları belirler.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Uygulama:</h3>
                  <p className="text-gray-700">İnce uçlu iğnelerle kas içine küçük dozlarda botoks enjekte edilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">İyileşme:</h3>
                  <p className="text-gray-700">Hasta hemen normal hayatına dönebilir. Tam etkiler 4–7 gün içinde ortaya çıkar.</p>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>💡 Önemli Not:</strong> Alın botoksu genellikle kaş arası ve göz çevresi botoksuyla birlikte uygulanır; bu, daha dengeli bir yüz ifadesi sağlar.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Alın Botoksunun Etkisi Ne Kadar Sürer?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                İlk etkiler birkaç gün içinde görülür ve en güçlü etki 2. haftada ortaya çıkar. Kalıcılığı kişiden kişiye değişmekle birlikte ortalama 4–6 ay devam eder. Düzenli uygulamalarda çizgilerin tamamen silinmesi mümkündür.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Alın Botoksu Fiyatları 2026
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Fiyatlar, kullanılan botoks markasına, muayenehane lokasyonuna ve uygulamayı yapan uzmana göre değişebilir. 2026 yılı için alın botoksu fiyatları şu aralıktadır:
              </p>

              {/* Price Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        İşlem
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Fiyat Aralığı (₺)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Alın Botoksu</td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold">2.500 – 4.000</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Kaş Arası + Alın</td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold">4.500 – 6.000</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Yaygın Sorular ve Merak Edilenler
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Alın botoksu doğal bir görünüm sağlar mı?</h3>
                  <p className="text-gray-700">Evet, doğru doz ve noktalara uygulandığında mimikler kaybolmaz, sadece çizgiler yumuşar.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Alın botoksu tehlikeli midir?</h3>
                  <p className="text-gray-700">Hayır. Sağlık Bakanlığı onaylı ürünler ve uzman doktorlarla yapılan uygulamalarda güvenlidir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. İlk defa alın botoksu yaptıran biri nelere dikkat etmeli?</h3>
                  <p className="text-gray-700">İlk 24 saat boyunca baş öne eğilmemeli, spor yapılmamalı, enjeksiyon bölgesine masaj uygulanmamalıdır.</p>
                </div>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Alın botoksu uygulaması sırasında enjeksiyon yapılan hasta"
                  fill
                  className="object-cover"
                />
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">Botoksun Yapılmaması Gereken Durumlar</h3>
                  <ul className="list-disc list-inside text-red-800 space-y-2">
                    <li>Hamilelik ve emzirme dönemi</li>
                    <li>Sinir-kas hastalıkları (örneğin: Myastenia Gravis)</li>
                    <li>Enjeksiyon bölgesinde aktif enfeksiyon</li>
                    <li>Botulinum toksinine karşı alerji</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Society of Plastic Surgeons – <a href="https://www.plasticsurgery.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.plasticsurgery.org</a></li>
                <li>Türk Estetik ve Plastik Cerrahi Derneği</li>
                <li>2026 Türkiye Muayenehane Fiyat Tarama Sonuçları</li>
                <li>Mayo Clinic – Botulinum Toxin Treatment Guide</li>
              </ul>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Alın Botoksu İçin Profesyonel Değerlendirme</h3>
              <p className="mb-6">Size uygun alın botoksu uygulaması için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Alın botoksu uygulaması hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <Link
              href="/tr/blog/botoks-fiyatlari-2026"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Fiyatları 2026</h4>
              <p className="text-gray-600 text-sm">Güncel botoks fiyatları ve nelere göre değiştiği hakkında detaylı bilgiler.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir? Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoks uygulaması, aşamaları ve etkileri hakkında kapsamlı bilgiler.</p>
            </Link>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <Link
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 